<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.dealership', 2), to: '/settings/dealerships' },
      {
        label: dealership.basicDetails.displayName,
        to: '/settings/dealerships/' + dealership.basicDetails.id
      }
    ]"
  >
    <template #right></template>
  </PageHeader>

  <div v-if="dealershipRequest.isLoading.value || dealership == null">
    <div class="grid sm:grid-cols-1 md:grid-cols-4 gap-x-8">
      <Skeleton
        width="100%"
        height="14rem"
      />
      <div class="md:col-span-3">
        <Skeleton
          width="100%"
          height="80vh"
        />
      </div>
    </div>
  </div>

  <div
    v-else
    class="grid sm:grid-cols-1 md:grid-cols-4 gap-x-8"
  >
    <div>
      <Tabs
        v-model="currentTab"
        :tabs="tabs"
        direction="vertical"
        type="pills-rail-block"
        class="mb-8 item"
      />
    </div>
    <div class="md:col-span-3">
      <div v-if="currentTab == 'basic'">
        <Card>
          <CardBody class="flex flex-col gap-6">
            <div>
              <CardTitle :title="$t('ui.common.basicInfo')" />
              <div class="flex items-center mb-8">
                <div class="flex flex-col gap-4">
                  <ToggleSwitch
                    v-model="dealership.basicDetails.isActive"
                    label="Active"
                    @change="onIsActiveToggleChange"
                  />
                  <ToggleSwitch
                    v-model="dealership.basicDetails.showInReports"
                    label="Show in Reports"
                  />
                </div>
                <!-- <div class="h-20 w-20 flex items-center justify-center bg-slate-100 rounded-full p-3">
                <img :src="manufacturerLogo" alt="" class="w-full" />
              </div> -->
              </div>

              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  :label="$t('ui.common.name')"
                  :is-required="v$.basicDetails.name.required"
                >
                  <Input
                    v-model="dealership.basicDetails.name"
                    :has-error="v$.basicDetails.name.$error"
                  />
                  <InputError :has-error="v$.basicDetails.name.$error">
                    {{ v$.basicDetails.name.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.common.displayName')"
                  :is-required="v$.basicDetails.displayName.required"
                >
                  <Input
                    v-model="dealership.basicDetails.displayName"
                    :has-error="v$.basicDetails.displayName.$error"
                  />
                  <InputError :has-error="v$.basicDetails.displayName.$error">
                    {{ v$.basicDetails.displayName.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.common.locationName')"
                  :is-required="v$.basicDetails.locationName.required"
                >
                  <Input
                    v-model="dealership.basicDetails.locationName"
                    :has-error="v$.basicDetails.locationName.$error"
                  />
                  <InputError :has-error="v$.basicDetails.locationName.$error">
                    {{ v$.basicDetails.locationName.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.common.dateTime.timezone')"
                  :is-required="v$.basicDetails.timeZone.required"
                >
                  <Select
                    v-model="dealership.basicDetails.timeZone"
                    mode="single"
                    searchable
                    :chips="false"
                    value-key="zoneName"
                    label-key="zoneName"
                    :options="timeZonesList"
                    :has-error="v$.basicDetails.timeZone.$error"
                  ></Select>
                  <InputError :has-error="v$.basicDetails.timeZone.$error">
                    {{ v$.basicDetails.timeZone.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>

              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  :label="$t('ui.entities.manufacturer')"
                  :is-required="v$.basicDetails.manufacturerId.required"
                >
                  <Select
                    v-model="dealership.basicDetails.manufacturerId"
                    mode="single"
                    searchable
                    value-key="value"
                    label-key="label"
                    :options="manufacturerOptions"
                    :can-deselect="false"
                    :has-error="v$.basicDetails.manufacturerId.$error"
                  >
                    <template #value="{ value }">
                      <div class="flex items-center">
                        <img
                          :src="value.logo"
                          width="25"
                          class="mr-3"
                        />
                        {{ value.label }}
                      </div>
                    </template>
                    <template #option="{ option }">
                      <div class="flex items-center">
                        <img
                          :src="option.logo"
                          width="20"
                          class="mr-3"
                        />
                        {{ option.label }}
                      </div>
                    </template>
                  </Select>
                  <InputError :has-error="v$.basicDetails.manufacturerId.$error">
                    {{ v$.basicDetails.manufacturerId.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.group')"
                  :is-required="v$.basicDetails.groupId.required"
                >
                  <Select
                    v-model="dealership.basicDetails.groupId"
                    mode="single"
                    value-key="value"
                    label-key="label"
                    searchable
                    :options="groupOptions"
                    :has-error="v$.basicDetails.groupId.$error"
                  ></Select>
                  <InputError :has-error="v$.basicDetails.groupId.$error">
                    {{ v$.basicDetails.groupId.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup :label="$t('ui.entities.comms.phoneNumber')">
                  <InputPhone v-model="dealership.basicDetails.telephone" />
                </FormGroup>
                <FormGroup :label="$t('ui.common.website')">
                  <Input
                    v-model="dealership.basicDetails.website"
                    icon="globe-pointer"
                  />
                </FormGroup>
              </div>
            </div>

            <div>
              <CardTitle
                title="Address"
                icon="location-dot"
              />
              <div class="grid md:grid-cols-2 gap-x-8">
                <!-- <FormGroup class="col-span-2" :label="$t('ui.common.search')">
                {{ addressSearchValue }}
                <Input v-model="addressSearchValue" icon="search" />
              </FormGroup> -->
                <FormGroup
                  :label="$t('ui.entities.address.line1')"
                  :is-required="v$.basicDetails.address.line1.required"
                >
                  <Input
                    v-model="dealership.basicDetails.address.line1"
                    :has-error="v$.basicDetails.address.line1.$error"
                  />
                  <InputError :has-error="v$.basicDetails.address.line1.$error">
                    {{ v$.basicDetails.address.line1.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.address.line2')"
                  :is-required="v$.basicDetails.address.line2.required"
                >
                  <Input
                    v-model="dealership.basicDetails.address.line2"
                    :has-error="v$.basicDetails.address.line2.$error"
                  />
                  <InputError :has-error="v$.basicDetails.address.line2.$error">
                    {{ v$.basicDetails.address.line2.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.address.city')"
                  :is-required="v$.basicDetails.address.city.required"
                >
                  <Input
                    v-model="dealership.basicDetails.address.city"
                    :has-error="v$.basicDetails.address.city.$error"
                  />
                  <InputError :has-error="v$.basicDetails.address.city.$error">
                    {{ v$.basicDetails.address.city.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.address.county')"
                  :is-required="v$.basicDetails.address.county.required"
                >
                  <Input
                    v-model="dealership.basicDetails.address.county"
                    :has-error="v$.basicDetails.address.county.$error"
                  />
                  <InputError :has-error="v$.basicDetails.address.county.$error">
                    {{ v$.basicDetails.address.county.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.address.postCode')"
                  :is-required="v$.basicDetails.address.postcode.required"
                >
                  <Input
                    v-model="dealership.basicDetails.address.postcode"
                    :has-error="v$.basicDetails.address.postcode.$error"
                  />
                  <InputError :has-error="v$.basicDetails.address.postcode.$error">
                    {{ v$.basicDetails.address.postcode.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.address.country')"
                  :is-required="v$.basicDetails.address.country.required"
                >
                  <Select
                    v-model="dealership.basicDetails.address.country"
                    mode="single"
                    value-key="code"
                    label-key="name"
                    searchable
                    :options="countriesList"
                    :has-error="v$.basicDetails.address.country.$error"
                  >
                    <template #value="{ value }">
                      <div class="flex items-center">
                        <img
                          :src="value.image"
                          width="25"
                          class="mr-3"
                        />
                        {{ value.name }}
                      </div>
                    </template>
                    <template #option="{ option }">
                      <div class="flex items-center">
                        <img
                          :src="option.image"
                          width="25"
                          class="mr-3"
                        />
                        {{ option.name }}
                      </div>
                    </template>
                  </Select>
                  <InputError :has-error="v$.basicDetails.address.country.$error">
                    {{ v$.basicDetails.address.country.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
            </div>

            <div>
              <CardTitle
                title="Notes"
                icon="file-lines"
              />
              <FormGroup>
                <TextArea
                  v-model="dealership.basicDetails.notes"
                  :rows="4"
                  placeholder="Notes"
                />
              </FormGroup>
            </div>
          </CardBody>

          <SectionFooter>
            <Button
              label="Save Changes"
              type="primary"
              :is-loading="formState.basicDetailsSubmitting"
              @click="submitBasicForm"
            />
          </SectionFooter>
        </Card>
      </div>

      <div v-if="currentTab == 'users'">
        <Card>
          <CardBody>
            <CardTitle
              title="Users"
              :has-margin="false"
            />
          </CardBody>
          <Separator no-margin />
          <TableBasic>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <!-- <th>Verification</th> -->
                <th>Email Address</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in users"
                :key="user.id"
              >
                <td class="flex items-center gap-3">
                  <Avatar
                    :url="user.profilePicUrl"
                    size="md"
                    :text="user.initials"
                  />
                  <div>
                    <div class="font-medium">{{ user.fullName }}</div>
                    <div class="text-sm">{{ user.jobTitle }}</div>
                  </div>
                </td>
                <td>
                  <Chip
                    v-if="user.isActive"
                    :rounded="false"
                    outlined
                    size="sm"
                    dot
                    severity="success"
                  >
                    Active
                  </Chip>
                  <Chip
                    v-else
                    :rounded="false"
                    outlined
                    size="sm"
                    dot
                    severity="danger"
                  >
                    Disabled
                  </Chip>
                </td>
                <!-- <td>
                  <Chip v-if="user.isVerified" :rounded="false" outlined size="sm" dot severity="success">Verified</Chip>
                  <Chip v-else :rounded="false" outlined size="sm" dot severity="danger">Not Verified</Chip>
                </td> -->
                <td>{{ user.email }}</td>
                <td>
                  <div class="flex justify-end">
                    <Button
                      v-tippy="$t('ui.entities.setting', 2)"
                      icon="cog"
                      plain
                      severity="secondary"
                      @click="$router.push('/settings/users/' + user.id)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </TableBasic>
        </Card>
      </div>

      <div v-if="currentTab == 'availability'">
        <Card>
          <CardBody>
            <AppointmentAvailabilitySettings
              v-model="dealership.availability"
              @changes-made="fetchAvailabilitySettings"
            />
          </CardBody>
        </Card>
      </div>

      <div v-if="currentTab == 'features'">
        <Card>
          <CardBody>
            <CardTitle
              title="Features"
              subtitle="Enabling/Disabling a feature will not alter this dealership's billing."
            />

            <div class="flex flex-col">
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.dataChangeAcknowledge"
                  label="Data Change Acknowledge"
                  description="Changes to customer data will require acknowledgement from the dealership to confirm that they have received the change"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.leads"
                  label="Leads"
                  description="Manage leads generated by external systems"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.leadsPostHandover"
                  label="Leads - Post Handover"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.leadsMotability"
                  label="Leads - Motability"
                  disabled
                  chip-label="Not in Use"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.usedStock"
                  label="Used Stock"
                  disabled
                  chip-label="Not in Use"
                  description="Allow importing of used stock from an external source"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.switch"
                  label="Switch"
                  disabled
                  description="Customer <-> Used Stock matching. Requires Used Stock to be enabled."
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.auction"
                  label="ReAuction"
                  description="Customers can put their vehicle up for auction on a dealership, group and national level"
                  chip-label="NEW!"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.reloop"
                  label="ReLoop"
                  description="Post-appointment emails asking customer for feedback"
                />
              </FormGroup>
              <FormGroup>
                <ToggleSwitch
                  v-model="dealership.features.advancedReporting"
                  label="Advanced Reporting"
                  description="Enhanced insights, reports and dashboards"
                  disabled
                  chip-label="Not in Use"
                />
              </FormGroup>
            </div>
          </CardBody>
          <SectionFooter>
            <Button
              label="Save Changes"
              type="primary"
              :is-loading="formState.featuresSubmitting"
              @click="submitFeaturesForm"
            />
          </SectionFooter>
        </Card>
      </div>

      <div v-if="currentTab == 'opening-hours'">
        <Card>
          <CardBody>
            <CardTitle :title="$t('ui.entities.opening-hours')" />
            <div class="flex flex-col gap-2">
              <div
                v-for="(dow, i) in dealership.openingHours?.sales"
                :key="i"
                class="grid grid-cols-7 lg:grid-cols-11 w-full gap-2"
              >
                <div class="flex items-center row-span-2">
                  {{
                    dayjs()
                      .day(dow.dayOfWeek + 1)
                      .format('ddd')
                  }}
                </div>
                <div class="flex items-center gap-2 col-span-4">
                  <Input
                    v-model="dow.from"
                    :readonly="dow.isClosed"
                    :placeholder="dow.isClosed ? 'Closed' : null"
                    class="w-full"
                  />
                  <Icon type="arrow-right" />
                  <Input
                    v-model="dow.to"
                    :readonly="dow.isClosed"
                    :placeholder="dow.isClosed ? 'Closed' : null"
                    class="w-full"
                  />
                </div>
                <div class="flex items-center col-span-2 gap-2 w-full ml-4">
                  <ToggleSwitch
                    v-model="dow.isClosed"
                    label="Closed"
                    @change="toggleDayClosed(i)"
                  />
                </div>
                <div class="col-span-6 lg:col-span-4">
                  <Input
                    v-model="dow.remarks"
                    placeholder="Add note"
                  />
                </div>
              </div>
            </div>
          </CardBody>
          <SectionFooter>
            <Button
              label="Save Changes"
              type="primary"
              :is-loading="formState.openingHoursSubmitting"
              @click="submitOpeningHoursForm"
            />
          </SectionFooter>
        </Card>
      </div>

      <div v-if="currentTab == 'billing'">
        <Card>
          <CardBody>
            <CardTitle title="Billing" />
            <ToggleSwitch
              v-model="dealership.billing.isActive"
              label="Enabled"
            />
          </CardBody>
          <SectionFooter>
            <Button
              label="Save Changes"
              type="primary"
              :is-loading="formState.billingSubmitting"
              @click="submitBillingForm"
            />
          </SectionFooter>
        </Card>

        <Card>
          <CardBody>
            <div class="flex justify-between items-start">
              <CardTitle
                title="Invoice Items"
                :subtitle="
                  dealership.billing?.invoiceTemplate ? 'All fees are pre-tax' : 'Not linked to any invoice templates'
                "
              />
              <div
                v-if="dealership.billing?.invoiceTemplate"
                class="flex gap-4 items-center"
              >
                <!-- <div class="text-muted">Template: #{{ dealership.billing?.invoiceTemplate?.id }}</div> -->
                <Button
                  outlined
                  severity="secondary"
                  :label="`View Invoice #${dealership.billing?.invoiceTemplate?.id}`"
                  @click="
                    $router.push('/settings/finance/repeating-invoice/' + dealership.billing?.invoiceTemplate?.id)
                  "
                />
              </div>
            </div>

            <Empty
              v-if="!dealership.billing?.invoiceTemplate"
              icon="link-slash"
              title="No Invoice Template Linked"
              description="This dealership does not have an invoice template linked"
            >
              <template #buttons>
                <Button
                  label="View Templates"
                  outlined
                  @click="$router.push('/settings/finance/repeating-invoices')"
                />
              </template>
            </Empty>

            <InvoiceItems
              v-if="dealership.billing?.invoiceTemplate"
              v-model="dealership.billing.invoiceTemplate.items"
              :invoice-template-id="dealership.billing?.invoiceTemplate?.id"
              :dealership-id="dealership.basicDetails.id"
              :catalog-items="invoiceCatalogItems"
              :read-only="!$can('manage_dealership_billing')"
              @item-validated="validateInvoiceItem"
              @item-removed="removeInvoiceItem"
              @item-added="addInvoiceItem"
            />
            <div
              v-if="dealership.billing?.invoiceTemplate"
              class="flex justify-end mt-4"
            >
              <div class="w-[150px] mr-[112px]">
                <div class="flex justify-between">
                  <div>Subtotal</div>
                  <div>{{ numberFormatter(billingTotals.subTotal, 'normal', 2) }}</div>
                </div>
                <div class="flex justify-between">
                  <div>Discount</div>
                  <div>-{{ numberFormatter(billingTotals.discount, 'normal', 2) }}</div>
                </div>
                <div class="flex justify-between">
                  <div>Tax</div>
                  <div>{{ numberFormatter(billingTotals.tax, 'normal', 2) }}</div>
                </div>
                <div class="flex justify-between font-semibold">
                  <div>Total</div>
                  <div>{{ numberFormatter(billingTotals.total, 'normal', 2) }}</div>
                </div>
              </div>
            </div>
          </CardBody>
          <SectionFooter v-if="dealership.billing?.invoiceTemplate">
            <Button
              label="Save Changes"
              type="primary"
              @click="saveInvoiceChanges"
            />
          </SectionFooter>
        </Card>
      </div>

      <div v-if="currentTab == 'config'">
        <Card>
          <CardBody class="flex flex-col gap-6">
            <div>
              <SectionLabel label="Customer Import" />
              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  label="Source"
                  :is-required="v$.config.importSource.required"
                >
                  <Select
                    v-model="dealership.config.importSource"
                    mode="single"
                    :options="['k2k', 'p360', 'blackhorse', 'emaster', 'evergreen-bank']"
                    :has-error="v$.config.importSource.$error"
                    :readonly="!$can('update_dealership_locked_data')"
                  ></Select>
                  <InputError :has-error="v$.config.importSource.$error">
                    {{ v$.config.importSource.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  label="Identifier"
                  :is-required="v$.config.importSourceId.required"
                >
                  <Input
                    v-model="dealership.config.importSourceId"
                    :has-error="v$.config.importSourceId.$error"
                    :readonly="!$can('update_dealership_locked_data')"
                  />
                  <InputError :has-error="v$.config.importSourceId.$error">
                    {{ v$.config.importSourceId.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
            </div>

            <div>
              <SectionLabel label="Used Stock" />
              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  label="Source"
                  :is-required="v$.config.usedStockLocationSource.required"
                >
                  <Select
                    v-model="dealership.config.usedStockLocationSource"
                    mode="single"
                    :close-on-select="true"
                    :options="['bluesky', 'gforces']"
                    :has-error="v$.config.usedStockLocationSource.$error"
                    :readonly="!$can('update_dealership_locked_data')"
                  ></Select>
                  <InputError :has-error="v$.config.usedStockLocationSource.$error">
                    {{ v$.config.usedStockLocationSource.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  label="Identifier"
                  :is-required="v$.config.usedStockLocationId.required"
                >
                  <Input
                    v-model="dealership.config.usedStockLocationId"
                    :has-error="v$.config.usedStockLocationId.$error"
                    :readonly="!$can('update_dealership_locked_data')"
                  />
                  <InputError :has-error="v$.config.usedStockLocationId.$error">
                    {{ v$.config.usedStockLocationId.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
            </div>

            <div>
              <SectionLabel
                label="ReAuction"
                class="mb-2"
              />
              <div class="grid md:grid-cols-2 gap-x-8">
                <FormGroup
                  label="Notify Email Addresses"
                  :is-required="v$.config.reAuctionNotificationEmails.required"
                  info="Enter an email address to notify when a new ReAuction is available.<br>Separate multiple email addresses with a comma."
                >
                  <Input
                    v-model="dealership.config.reAuctionNotificationEmails"
                    :has-error="v$.config.reAuctionNotificationEmails.$error"
                  />
                  <InputError :has-error="v$.config.reAuctionNotificationEmails.$error">
                    {{ v$.config.reAuctionNotificationEmails.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
            </div>
          </CardBody>
          <SectionFooter>
            <Button
              label="Save Changes"
              type="primary"
              :is-loading="formState.configSubmitting"
              @click="submitConfigForm"
            />
          </SectionFooter>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import Select from '@/components/forms/Select.vue'
import { useAbility } from '@casl/vue'

import Skeleton from '@/components/skeleton/Skeleton.vue'

import useApiRequest from '@/composables/useApiRequest'
import { useAuthStore } from '@/stores/AuthStore'
import { alertToast } from '@/utilities/notification'
import { timeZones } from '@/utilities/date-time'
import { getAllCountries } from '@/utilities/countries.ts'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Tabs from '@/components/tabs/Tabs.vue'
import Input from '@/components/forms/Input.vue'
import Icon from '@/components/icon/Icon.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import InputError from '@/components/forms/InputError.vue'
import Button from '@/components/button/Button.vue'
import TableBasic from '@/components/tables/TableBasic.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Chip from '@/components/chip/Chip.vue'
import Separator from '@/components/separator/Separator.vue'
import InputPhone from '@/components/forms/InputPhone.vue'
import TextArea from '@/components/forms/TextArea.vue'
import AppointmentAvailabilitySettings from '@/components/unique/AppointmentAvailabilitySettings.vue'

import SectionFooter from '@/components/sections/SectionFooter.vue'
import SectionLabel from '@/components/sections/SectionLabel.vue'

import InvoiceItems from '@/components/invoice/InvoiceItems.vue'
import Empty from '@/components/empty/Empty.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { can } = useAbility()

const authStore = useAuthStore()
const timeZonesList = timeZones()
const countriesList = getAllCountries()

const dealershipRequest = useApiRequest()
const dealershipAvailabilityRequest = useApiRequest()
const dealershipSubmitFormRequest = useApiRequest()

const users = ref([])
const invoiceCatalogItems = ref([])

useApiRequest()
  .send({
    endpoint: '/v1/users',
    params: { userType: 'externalUsers', dealerships: route.params.id }
  })
  .then(response => {
    users.value = response.data
  })

// Fetch invoice catalog items
useApiRequest()
  .send({ endpoint: '/v1/finance/invoice-items-catalog' })
  .then(response => {
    invoiceCatalogItems.value = response.data
  })

const manufacturerOptions = ref([])
const groupOptions = ref([])

useApiRequest()
  .send({ endpoint: '/v1/manufacturers?showHidden=true' })
  .then(response => {
    manufacturerOptions.value = response.data.map(manufacturer => {
      return {
        value: manufacturer.id,
        label: manufacturer.name,
        logo: manufacturer.logoUrl
      }
    })
  })

useApiRequest()
  .send({ endpoint: '/v1/groups' })
  .then(response => {
    groupOptions.value = response.data.map(group => {
      return {
        value: group.id,
        label: group.name,
        logo: group.logoUrl
      }
    })
  })

const dealership = ref({
  basicDetails: {
    id: null,
    name: null,
    displayName: null,
    locationName: null,
    manufacturerId: null,
    manufacturerName: null,
    groupId: null,
    groupName: null,
    telephone: null,
    website: null,
    timeZone: null,
    address: {
      line1: null,
      line2: null,
      line3: null
    },
    isActive: null,
    showInReports: null,
    notes: null
  },
  comms: {},
  features: {
    dataChangeAcknowledge: null,
    leads: null,
    leadsPostHandover: null,
    leadsMotability: null,
    advancedReporting: null,
    switch: null,
    usedStock: null,
    auction: null,
    reloop: null
  },
  billing: {
    isActive: null,
    invoiceTemplate: {
      id: null,
      items: []
    }
  },
  config: {
    importSource: null,
    importSourceId: null,
    usedStockLocationSource: null,
    usedStockLocationId: null
  }
})

const openingHoursConfig = {
  sales: [
    {
      dayOfWeek: 0,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 1,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 2,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 3,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 4,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 5,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    },
    {
      dayOfWeek: 6,
      from: null,
      to: null,
      isClosed: false,
      remarks: null
    }
  ]
}

function fetchDealershipData() {
  dealershipRequest
    .send({
      endpoint: '/v1/dealerships/' + route.params.id,
      method: 'GET'
    })
    .then(response => {
      // Basic Details
      dealership.value.basicDetails = {
        id: response.data.id,
        name: response.data.name,
        displayName: response.data.displayName,
        locationName: response.data.locationName,
        manufacturerId: response.data.manufacturerId,
        manufacturerName: response.data.manufacturerName,
        groupId: response.data.groupId,
        groupName: response.data.groupName,
        telephone: response.data.telephone,
        website: response.data.website,
        timeZone: response.data.timeZone,
        isActive: response.data.isActive,
        showInReports: response.data.showInReports,
        notes: response.data.notes ?? null,
        address: {
          line1: response.data.address.line1,
          line2: response.data.address.line2,
          line3: response.data.address.line3,
          city: response.data.address.city,
          county: response.data.address.county,
          postcode: response.data.address.postcode,
          country: response.data.address.country
        }
      }

      // Features
      dealership.value.features = {
        dataChangeAcknowledge: response.data.features.dataChangeAcknowledge,
        leads: response.data.features.leads,
        leadsPostHandover: response.data.features.leadsPostHandover,
        leadsMotability: response.data.features.leadsMotability,
        advancedReporting: response.data.features.advancedReporting,
        switch: response.data.features.switch,
        usedStock: response.data.features.usedStock,
        auction: response.data.features.auction,
        reloop: response.data.features.reloop
      }

      // Opening Hours
      dealership.value.openingHours = {
        sales: response.data.openingHours.sales.length ? response.data.openingHours.sales : openingHoursConfig.sales
      }

      // Billing
      dealership.value.billing = {
        isActive: response.data.config.billingIsActive
      }

      // config
      dealership.value.config = {
        importSource: response.data.config.importSource,
        importSourceId: response.data.config.importSourceId,
        usedStockLocationSource: response.data.config.usedStockLocationSource,
        usedStockLocationId: response.data.config.usedStockLocationId,
        reAuctionNotificationEmails: response.data.config.reAuctionNotificationEmails
      }

      // Fetch invoice items
      useApiRequest()
        .send({
          endpoint: '/v1/finance/invoice-templates',
          method: 'GET',
          params: { dealerships: route.params.id }
        })
        .then(response => {
          if (response.success && response.data[0]) {
            // Get the invoice template for this dealership
            useApiRequest()
              .send({
                endpoint: '/v1/finance/invoice-templates/' + response.data[0].id,
                method: 'GET'
              })
              .then(response => {
                if (response.success) {
                  dealership.value.billing.invoiceTemplate = {
                    ...response.data,
                    items: response.data.items?.map(item => ({
                      ...item,
                      randId: Math.random(),
                      isValid: false
                    }))
                  }

                  const dealershipSpecificItems = dealership.value.billing.invoiceTemplate?.items?.filter(
                    i => i.invoiceTemplateDealershipId == route.params.id
                  )
                  dealership.value.billing.invoiceTemplate.items = dealershipSpecificItems
                }
              })
          }
        })

      fetchAvailabilitySettings()
    })
}
fetchDealershipData()

// Watch for changes in the id in the route, and if it changes, fetch the new user data
watch(
  () => route.params.id,
  () => {
    fetchDealershipData()
  }
)

function fetchAvailabilitySettings() {
  dealershipAvailabilityRequest
    .send({ endpoint: '/v1/availability/settings?dealershipId=' + route.params.id, method: 'GET' })
    .then(response => {
      if (response.success) {
        dealership.value.availability = response.data

        dealership.value.availability = dealership.value.availability.sort(function (a, b) {
          // Sort by day of week
          if (a.dayOfWeek < b.dayOfWeek) return -1
          if (a.dayOfWeek > b.dayOfWeek) return 1
          return 0
        })
      }
    })
}

const currentTab = ref(route.params.tab)
const tabs = ref([
  {
    id: 'basic',
    label: t('ui.common.basicInfo'),
    icon: 'building',
    to: '/settings/dealerships/' + route.params.id
  },
  {
    id: 'users',
    label: t('ui.common.user', 2),
    icon: 'users',
    to: '/settings/dealerships/' + route.params.id + '/users',
    isHidden: computed(() => !can('manage_users'))
  },
  {
    id: 'availability',
    label: `${t('ui.entities.appointment.label')} ${t('ui.entities.availability')}`,
    icon: 'calendar-check',
    to: '/settings/dealerships/' + route.params.id + '/availability',
    isHidden: false
  },
  {
    id: 'features',
    label: t('ui.entities.features'),
    icon: 'star',
    to: '/settings/dealerships/' + route.params.id + '/features',
    isHidden: computed(() => authStore.details.userType != 1)
  },
  {
    id: 'opening-hours',
    label: t('ui.entities.openingHours'),
    icon: 'clock',
    to: '/settings/dealerships/' + route.params.id + '/opening-hours',
    isHidden: false
  },
  {
    id: 'billing',
    label: t('ui.entities.finance.billing.label'),
    icon: 'credit-card',
    to: '/settings/dealerships/' + route.params.id + '/billing',
    isHidden: computed(() => authStore.details.userType != 1 && !can('manage_dealership_billing'))
  },
  {
    id: 'config',
    label: t('ui.common.configuration'),
    icon: 'cog',
    to: '/settings/dealerships/' + route.params.id + '/config',
    isHidden: computed(() => authStore.details.userType != 1)
  }
])

function toggleDayClosed(i) {
  if (dealership.value.openingHours.sales[i].isClosed) {
    dealership.value.openingHours.sales[i].from = null
    dealership.value.openingHours.sales[i].to = null
  }
}

// Set tab to 'Basic' if no tab is set
if (route.params.tab === '') {
  currentTab.value = 'basic'
} else {
  currentTab.value = route.params.tab
}

// If the user changes the tab, update the route
watch(
  () => currentTab.value,
  newTab => {
    currentTab.value = newTab
    // fetch tab route
    const tab = tabs.value.find(tab => tab.id === newTab)
    if (tab) {
      router.replace(tab.to)
    }
  }
)

const inputRules = {
  basicDetails: {
    name: { required },
    displayName: { required },
    locationName: { required },
    manufacturerId: { required },
    groupId: { required },
    telephone: {},
    website: {},
    timeZone: { required },
    address: {
      line1: { required },
      line2: {},
      line3: {},
      city: { required },
      county: { required },
      postcode: { required },
      country: { required }
    }
  },
  features: {
    dataChangeAcknowledge: {},
    leads: {},
    leadsPostHandover: {},
    leadsMotability: {},
    advancedReporting: {},
    switch: {},
    usedStock: {},
    auction: {},
    reloop: {}
  },
  billing: {
    isActive: {}
  },
  config: {
    importSource: {},
    importSourceId: {},
    usedStockLocationSource: {},
    usedStockLocationId: {},
    reAuctionNotificationEmails: {}
  }
}

const v$ = useVuelidate(inputRules, dealership)

const formState = ref({
  basicDetailsSubmitting: false,
  featuresSubmitting: false,
  billingSubmitting: false,
  openingHoursSubmitting: false,
  configSubmitting: false
})

async function submitBasicForm() {
  const isFormCorrect = await v$.value.basicDetails.$validate()

  if (isFormCorrect) {
    formState.value.basicDetailsSubmitting = true

    dealershipSubmitFormRequest
      .send({
        endpoint: '/v1/dealerships/' + route.params.id,
        method: 'PATCH',
        data: {
          name: dealership.value.basicDetails.name,
          displayName: dealership.value.basicDetails.displayName,
          locationName: dealership.value.basicDetails.locationName,
          manufacturerID: dealership.value.basicDetails.manufacturerId,
          groupID: dealership.value.basicDetails.groupId,
          telephone: dealership.value.basicDetails.telephone,
          website: dealership.value.basicDetails.website,
          timeZone: dealership.value.basicDetails.timeZone,
          address: {
            line1: dealership.value.basicDetails.address.line1,
            line2: dealership.value.basicDetails.address.line2,
            line3: dealership.value.basicDetails.address.line3,
            city: dealership.value.basicDetails.address.city,
            county: dealership.value.basicDetails.address.county,
            postcode: dealership.value.basicDetails.address.postcode,
            country: dealership.value.basicDetails.address.country
          },
          isActive: dealership.value.basicDetails.isActive,
          showInReports: dealership.value.basicDetails.showInReports,
          notes: dealership.value.basicDetails.notes
        }
      })
      .then(response => {
        if (response.success) {
          formState.value.basicDetailsSubmitting = false

          alertToast('Details updated', null, 'success')
        }
      })
  }
}

async function submitFeaturesForm() {
  const isFormCorrect = await v$.value.features.$validate()

  if (isFormCorrect) {
    formState.value.featuresSubmitting = true

    dealershipSubmitFormRequest
      .send({
        endpoint: '/v1/dealerships/' + route.params.id,
        method: 'PATCH',
        data: {
          features: {
            dataChangeAcknowledge: dealership.value.features.dataChangeAcknowledge,
            leads: dealership.value.features.leads,
            leadsPostHandover: dealership.value.features.leadsPostHandover,
            leadsMotability: dealership.value.features.leadsMotability,
            advancedReporting: dealership.value.features.advancedReporting,
            switch: dealership.value.features.switch,
            usedStock: dealership.value.features.usedStock,
            auction: dealership.value.features.auction,
            reloop: dealership.value.features.reloop
          }
        }
      })
      .then(response => {
        if (response.success) {
          formState.value.featuresSubmitting = false

          // Notification
          alertToast('Features Updated', null, 'success')
        }
      })
  }
}

async function submitBillingForm() {
  const isFormCorrect = await v$.value.billing.$validate()

  if (isFormCorrect) {
    formState.value.billingSubmitting = true

    dealershipSubmitFormRequest
      .send({
        endpoint: '/v1/dealerships/' + route.params.id,
        method: 'PATCH',
        data: {
          config: {
            billingIsActive: dealership.value.billing.isActive
          }
        }
      })
      .then(response => {
        if (response.success) {
          formState.value.billingSubmitting = false

          // Notification
          alertToast('Updated', null, 'success')
        }
      })
  }
}

async function submitOpeningHoursForm() {
  formState.value.openingHoursSubmitting = true
  dealershipSubmitFormRequest
    .send({
      endpoint: '/v1/dealerships/' + route.params.id,
      method: 'PATCH',
      data: {
        openingHours: {
          sales: dealership.value.openingHours.sales
        }
      }
    })
    .then(response => {
      if (response.success) {
        formState.value.openingHoursSubmitting = false

        // Notification
        alertToast('Updated', null, 'success')
      }
    })
  // }
}

async function submitConfigForm() {
  const isFormCorrect = await v$.value.config.$validate()

  if (isFormCorrect) {
    formState.value.configSubmitting = true

    dealershipSubmitFormRequest
      .send({
        endpoint: '/v1/dealerships/' + route.params.id,
        method: 'PATCH',
        data: {
          config: {
            importSource: dealership.value.config.importSource,
            importSourceId: dealership.value.config.importSourceId,
            usedStockLocationSource: dealership.value.config.usedStockLocationSource,
            usedStockLocationId: dealership.value.config.usedStockLocationId,
            reAuctionNotificationEmails: dealership.value.config.reAuctionNotificationEmails
          }
        }
      })
      .then(response => {
        if (response.success) {
          formState.value.configSubmitting = false

          // Notification
          alertToast('Updated', null, 'success')
        }
      })
  }
}

function onIsActiveToggleChange(value) {
  if (value == false) {
    dealership.value.basicDetails.showInReports = false
  }
}

const billingTotals = computed(() => {
  const totals = {
    subTotal: 0,
    discount: 0,
    tax: 0,
    total: 0
  }
  dealership.value.billing.invoiceTemplate?.items?.forEach(item => {
    const itemRate = item.overrideRate ? item.overrideRate : item.catalogItem.saleRate

    totals.subTotal += itemRate * item.quantity

    let discount
    if (item.discountValue && item.discountUnit == 'percent') {
      discount = (item.discountValue / 100) * item.quantity * itemRate
    } else {
      discount = item.discountValue
    }
    totals.discount += discount

    totals.tax += item.catalogItem.taxRate * (itemRate * item.quantity - discount)
  })
  totals.total = totals.subTotal - totals.discount + totals.tax

  return totals
})

const invoiceItemsValid = ref(null)

function validateInvoiceItem(valid) {
  invoiceItemsValid.value = valid
}

function removeInvoiceItem(randId) {
  const item = dealership.value.billing.invoiceTemplate.items.find(item => item.randId == randId)
  const index = dealership.value.billing.invoiceTemplate.items.indexOf(item)
  if (index != -1) {
    dealership.value.billing.invoiceTemplate.items.splice(index, 1)
  }
}

function addInvoiceItem(dealershipId, item) {
  dealership.value.billing.invoiceTemplate.items.push(item)
}

const updateInvoiceItemsRequest = useApiRequest()
function saveInvoiceChanges() {
  if (!invoiceItemsValid.value) {
    return
  }
  const formattedItems = []

  // build the invoice items to send to the api
  dealership.value.billing.invoiceTemplate.items.forEach(item => {
    formattedItems.push({
      catalogItemId: item.catalogItemId,
      overrideRate: item.overrideRate == '' ? null : item.overrideRate,
      quantity: item.quantity,
      discountUnit: item.discountUnit,
      discountValue:
        item.discountValue == '' ? null : item.discountUnit == 'percent' ? item.discountValue / 100 : item.discountValue
    })
  })

  const dataToPost = JSON.stringify({
    items: formattedItems
  })

  try {
    updateInvoiceItemsRequest
      .send({
        endpoint:
          '/v1/finance/invoice-templates/' +
          dealership.value.billing.invoiceTemplate.id +
          '/dealerships/' +
          dealership.value.basicDetails.id +
          '/items',
        method: 'PUT',
        data: dataToPost
      })
      .then(response => {
        if (response.success) {
          // Notification
          alertToast('Updated', response.message, 'success')
        } else {
          alertToast('Failed to update invoice items', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update invoice items', err.message, 'error')
  }
}
</script>

<style></style>
